import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

import {
  InputElement,
  InputRadio,
  InputRadioWrapper,
  InputWrapper,
  Label,
  LabelRadio,
  LabelText,
} from './styles'

const InputLine = ({
  ariaAutocomplete,
  ariaExpanded,
  autoComplete,
  className,
  checked,
  disabled,
  error,
  formBackground = 'white',
  id,
  inputRef,
  isFocused,
  label,
  last,
  name,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  placeholder,
  readOnly,
  role,
  type,
  value,
  icon,
  inputmode,
  pattern,
}) => {
  const ref = useRef(inputRef)
  const [hasFocus, setFocus] = useState(false)
  const [ssrDone, setSsrDone] = useState(false)

  useEffect(() => {
    setSsrDone(true)

    if (document.hasFocus() && ref?.current?.contains?.(document.activeElement)) {
      setFocus(true)
    }
  }, [])

  if (type === 'radio') {
    return (
      <InputRadioWrapper>
        <LabelRadio htmlFor={id}>
          <InputRadio
            aria-autocomplete={ariaAutocomplete}
            aria-expanded={ariaExpanded}
            autoComplete={autoComplete}
            data-testid={id}
            disabled={disabled}
            id={id}
            name={name}
            onBlur={onBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            readOnly={readOnly}
            ref={ref}
            role={role}
            type={type}
            value={value}
            checked={checked}
          />
          {label}
        </LabelRadio>
      </InputRadioWrapper>
    )
  }

  return (
    <InputWrapper
      isFocused={isFocused || hasFocus}
      className={className}
      error={error}
      last={last}
      background={formBackground}
      disabled={disabled}
    >
      {icon}
      <Label htmlFor={id} withIcon={!!icon}>
        <InputElement
          aria-autocomplete={ariaAutocomplete}
          aria-expanded={ariaExpanded}
          autoComplete={autoComplete}
          data-testid={id}
          disabled={disabled}
          id={id}
          name={name}
          onChange={onChange}
          onBlur={event => {
            setFocus(false)
            return onBlur && onBlur(event)
          }}
          onFocus={event => {
            setFocus(true)
            return onFocus && onFocus(event)
          }}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          readOnly={readOnly}
          ref={ref}
          role={role}
          type={type}
          inputmode={inputmode}
          pattern={pattern}
          value={value}
        />

        <LabelText
          withIcon={!!icon}
          isTransitioned={(isFocused || hasFocus || value) && ssrDone}
          background={disabled || !value ? 'transparent' : formBackground}
        >
          {label}
        </LabelText>
      </Label>
    </InputWrapper>
  )
}

InputLine.propTypes = {
  ariaAutocomplete: PropTypes.string,
  ariaExpanded: PropTypes.string,
  autoComplete: PropTypes.string,
  checked: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  formBackground: PropTypes.oneOf(['white', 'grey7']),
  icon: PropTypes.instanceOf(Object),
  id: PropTypes.string,
  inputRef: PropTypes.func,
  isFocused: PropTypes.bool,
  label: PropTypes.node,
  last: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  role: PropTypes.string,
  type: PropTypes.oneOf([
    'text',
    'number',
    'search',
    'email',
    'tel',
    'checkbox',
    'labeled',
    'password',
    'radio',
  ]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputmode: PropTypes.string,
  pattern: PropTypes.string,
}

export default InputLine
